import React, { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Grid, Button, Box, Card } from '@material-ui/core';
import BlockUserModal from '@screen/UserList/BlockUserModal';
import styled, { css } from 'styled-components';

import { EmptyList, ShowDotsMenu } from '@components';
import { capitalize, getRolPriority } from '@helpers';
import * as Routes from '@constants/routes';
import { useAuth, useUser, useBreakpoint } from '@hooks';
import {
  ADMIN,
  ADMIN_SECONDARY,
  SELLER,
  SUPER_ADMIN_ONUS,
  ADMIN_ONUS,
  ANALISTA_ONUS,
  USER_STATUS,
  STATUS_LIST_PRIORITY,
} from '@constants/user';
import { UserStatus } from '@components/UserStatus';
import { InputSearch, Select } from 'fpay-ui';
import classes from './UserList.module.scss';
import { getHtmlIdBlockUserModal } from '../../helpers/getHtmlId';

const { UNSUBSCRIBE, APPROVED, DELETED } = USER_STATUS;

const SelectStyle = styled(Select)(
  ({ isMobile }) => css`
    width: 155px;
    height: 48px;
    ${isMobile
      ? css`
          width: 100%;
          margin-top: 8px;
        `
      : css`
          margin-left: 16px;
        `}
    >div>div {
      color: #a2a7ba;
    }
  `
);

const UserList = () => {
  const { t } = useTranslation();
  const { mobile } = useBreakpoint();
  const {
    users,
    resendInvitation,
    selectUser,
    unsubscribeUser,
    deleteUser,
    filter: { searchName, selectedRol },
    changeFilter,
    resetFilter,
    getPermissionActionsByUser,
  } = useUser();

  const [savedSearchName, setSavedSearchName] = useState(searchName);
  const [confirmModal, setConfirmModal] = useState(false);
  const { isOnus, roles: currentUserRoles } = useAuth();

  const history = useHistory();

  const AllRoles = t('users.filter.allRoles');

  const roleCategory = isOnus ? 'onus' : 'nonOnus';

  const roleHierarchy = {
    onus: {
      'SUPER-ADMIN-ONUS': 1,
      'ADMIN-ONUS': 2,
      ONUS: 3,
      'ANALISTA-ONUS': 4,
    },
    nonOnus: {
      ADMIN: 1,
      LEGAL_REPRESENTATIVE: 1,
      'ADMIN-SECONDARY': 2,
      SELLER: 3,
    },
  };

  const validCurrentUserRoles = currentUserRoles.filter(
    (role) => roleHierarchy[roleCategory][role] !== undefined
  );

  const currentUserRoleLevel = Math.min(
    ...validCurrentUserRoles.map((role) => roleHierarchy[roleCategory][role])
  );

  // Filtrar usuarios cuyos roles coincidan con los válidos y verificar el nivel de jerarquía
  const filteredUsers = users.filter((user) =>
    user.roles.some((userRole) => {
      const userRoleLevel = roleHierarchy[roleCategory][userRole];
      return userRoleLevel !== undefined && userRoleLevel >= currentUserRoleLevel;
    })
  );

  const [cleanListUsers, setCleanListUsers] = useState(filteredUsers);

  const matchOptionName = (name, searchText) => capitalize(name).includes(capitalize(searchText));

  useEffect(() => {
    resetFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortUsersRolPriority = (a, b) => getRolPriority(a) - getRolPriority(b);
  const sortUsersStatusPriority = (a, b) =>
    STATUS_LIST_PRIORITY[a.status] - STATUS_LIST_PRIORITY[b.status];

  useEffect(() => {
    setSavedSearchName(searchName);
    const newList = filteredUsers
      .filter((user) => user.status !== DELETED)
      .filter(
        (user) =>
          (searchName === '' ||
            matchOptionName(
              `${capitalize(user.first_name)} ${capitalize(user.last_name)}`,
              searchName
            )) &&
          (selectedRol === '' ||
            selectedRol === AllRoles ||
            user.is_legal_agent ||
            user.roles.filter((rol) => rol === selectedRol).length !== 0)
      )
      .sort(sortUsersStatusPriority)
      .sort(sortUsersRolPriority);
    setCleanListUsers(newList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchName, selectedRol]);

  const [deleteBlockUser, setDeleteBlockUser] = useState({
    status: '',
    action: '',
    first_name: '',
    last_name: '',
  });

  const getOnusOptions = (userRole) => {
    switch (userRole) {
      case 'SUPER-ADMIN-ONUS':
        return [
          { value: SUPER_ADMIN_ONUS, label: t('roles.SUPER-ADMIN-ONUS') },
          { value: ADMIN_ONUS, label: t('roles.ADMIN-ONUS') },
          { value: ANALISTA_ONUS, label: t('roles.ANALISTA-ONUS') },
        ];
      case 'ADMIN-ONUS':
        return [
          { value: ADMIN_ONUS, label: t('roles.ADMIN-ONUS') },
          { value: ANALISTA_ONUS, label: t('roles.ANALISTA-ONUS') },
        ];
      case 'ONUS':
      case 'ANALISTA-ONUS':
        return [{ value: ANALISTA_ONUS, label: t('roles.ANALISTA-ONUS') }];
      default:
        return [];
    }
  };

  const getNonOnusOptions = (userRole) => {
    switch (userRole) {
      case 'ADMIN':
      case 'LEGAL_REPRESENTATIVE':
        return [
          { value: ADMIN, label: t('roles.ADMIN') },
          { value: ADMIN_SECONDARY, label: t('roles.ADMIN-SECONDARY') },
          { value: SELLER, label: t('roles.SELLER') },
        ];
      case 'ADMIN-SECONDARY':
        return [
          { value: ADMIN_SECONDARY, label: t('roles.ADMIN-SECONDARY') },
          { value: SELLER, label: t('roles.SELLER') },
        ];
      case 'SELLER':
        return [{ value: SELLER, label: t('roles.SELLER') }];
      default:
        return [];
    }
  };

  const getHighestRole = (userRoles, isOnusBoolean) => {
    const hierarchy = isOnusBoolean ? roleHierarchy.onus : roleHierarchy.nonOnus;

    return userRoles.reduce((highestRole, currentRole) => {
      if (hierarchy[currentRole] !== undefined) {
        if (!highestRole || hierarchy[currentRole] < hierarchy[highestRole]) {
          return currentRole;
        }
      }
      return highestRole;
    }, null);
  };

  const highestUserRole = getHighestRole(validCurrentUserRoles, isOnus);

  const optionsRolOnthem = !isOnus ? getNonOnusOptions(highestUserRole) : [];
  const optionsRolOnus = isOnus ? getOnusOptions(highestUserRole) : [];

  const optionsRol = [{ value: AllRoles, label: AllRoles }, ...optionsRolOnthem, ...optionsRolOnus];

  const setModalText = () => {
    if (deleteBlockUser.action === UNSUBSCRIBE) {
      return t('users.actions.answerBlock', {
        first_name: capitalize(deleteBlockUser?.first_name),
        last_name: capitalize(deleteBlockUser.last_name),
      });
    }
    if (deleteBlockUser.action === DELETED) {
      return t('users.actions.answerDelete', {
        first_name: capitalize(deleteBlockUser?.first_name),
        last_name: capitalize(deleteBlockUser.last_name),
      });
    }
    return t('users.actions.answerUnblock', {
      first_name: capitalize(deleteBlockUser?.first_name),
      last_name: capitalize(deleteBlockUser.last_name),
    });
  };

  const handleDeleteBlockClick = (user, action) => {
    setDeleteBlockUser({ ...user, action });
    setConfirmModal(true);
  };

  const handleConfirmBlock = () => {
    if (deleteBlockUser.action === DELETED) {
      deleteUser(deleteBlockUser);
      setConfirmModal(false);
      return;
    }
    unsubscribeUser(deleteBlockUser);
    setConfirmModal(false);
  };

  const handleEdit = (user) => {
    selectUser(user);
    history.push(Routes.USER_EDIT);
  };

  const handleSelectUser = (user) => {
    selectUser(user);
    history.push(Routes.USER_DETAIL);
  };

  const selectSearchUser = (text) => {
    if (text !== '') {
      resetFilter();
    }
    changeFilter({ searchName: text });
  };

  const handleSelectOnChange = (e) => {
    resetFilter();
    changeFilter({ selectedRol: e.target.value });
  };

  const showDots = (user) => {
    const { canResendInvitation, canEdit, canBlock, canUnblock, canDelete } =
      getPermissionActionsByUser(user);
    const verifiedUser = user.status === APPROVED;
    if (!canResendInvitation && !canEdit && !canBlock && !canUnblock && !canDelete) return null;

    return (
      <div className={classes.dotsContainer} data-testid="dots-container">
        <ShowDotsMenu
          htmlIdDeleteItem={verifiedUser ? 'delete-collaborator' : 'delete-pending-collaborator'}
          htmlIdBlockItem="block-collaborator"
          htmlIdUnBlockItem="unblock-collaborator"
          htmlIdEditItem={verifiedUser ? 'edit-collaborator' : 'edit-pending-collaborator'}
          htmlIdResendInvitationItem="resend-invitation-collaborator"
          {...{ canResendInvitation, canEdit, canBlock, canUnblock, canDelete }}
          {...{
            handleResendInvitation: () => resendInvitation(user.email),
            handleEdit: () => handleEdit(user),
            handleBlock: () => handleDeleteBlockClick(user, UNSUBSCRIBE),
            handleUnblock: () => handleDeleteBlockClick(user, APPROVED),
            handleDelete: () => handleDeleteBlockClick(user, DELETED),
          }}
        />
      </div>
    );
  };

  return (
    <>
      {filteredUsers.filter((user) => user.status !== DELETED).length > 0 && (
        <Grid container className={classes.userlist}>
          <Grid item container xs={12} className={classes.titleBar}>
            <Grid item xs={8}>
              <h6>{t('users.screen.title')}</h6>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" justifyContent="flex-end">
                <Link to={Routes.USER_INVITE_FORM}>
                  <Button id="invite-collaborator" size="small">
                    {t('users.list.invite')}
                  </Button>
                </Link>
              </Box>
            </Grid>
          </Grid>
          <Grid item container xs={12} className={classes.filters}>
            <InputSearch
              options={users
                .filter((user) => user.status !== DELETED)
                .sort(sortUsersStatusPriority)
                .sort(sortUsersRolPriority)
                .map((user) => `${capitalize(user.first_name)} ${capitalize(user.last_name)}`)}
              idMovement={selectSearchUser}
              clearFilter
              handleCleanFilterSearch={() => {
                selectSearchUser('');
              }}
              noMatchText={t('users.filter.noMatchText')}
              placeHolder={t('users.filter.placeholderSearchUser')}
              placeHolderColor="#A2A7BA"
              fontColorTextMark="#43B02A"
              limitRenderOptions={6}
              matchOption={matchOptionName}
              handleChange={(e) => {
                setSavedSearchName(e.target.value);
              }}
              value={savedSearchName}
            />
            <SelectStyle
              isMobile={mobile}
              placeholder={t('users.filter.placeholderRol')}
              options={optionsRol}
              onChange={handleSelectOnChange}
              value={selectedRol}
            />
          </Grid>
          <Grid item xs={12} sm={12} className={classes.cards} data-testid="users-cards-container">
            <Grid item xs={11} className={classes.header} container>
              <Grid item xs={4}>
                {t('users.header.username')}
              </Grid>
              <Grid item xs={4}>
                {t('users.header.email')}
              </Grid>
              <Grid item xs={2}>
                {t('users.header.rol')}
              </Grid>
              <Grid item xs={1}>
                {t('users.header.status')}
              </Grid>
            </Grid>
            {Array.isArray(cleanListUsers) && cleanListUsers.length !== 0 ? (
              cleanListUsers.map((user, i) => (
                <Card className={classes.card} key={i} data-testid="user-card">
                  <Grid container xs={12} sm={12}>
                    <Grid
                      onClick={() => handleSelectUser(user)}
                      item
                      xs={11}
                      sm={11}
                      md={11}
                      container
                      className={classes.details}
                    >
                      <Grid
                        id="name-user"
                        item
                        xs={11}
                        sm={3}
                        md={4}
                        data-testid="username-container"
                      >
                        <h6 data-testid="username">
                          {capitalize(user.first_name)} {capitalize(user.last_name)}
                        </h6>
                      </Grid>
                      <Grid id="user-mail" item xs={11} sm={5} md={4}>
                        <p className={classes.isEmail}>
                          <span data-testid="rl-rol" className={classes.isEmail}>
                            {user.email}
                          </span>
                        </p>
                      </Grid>
                      <Grid
                        id="user-roles"
                        item
                        xs={11}
                        sm={2}
                        md={2}
                        data-testid="user-roles-container"
                      >
                        <p>
                          <span data-testid="rl-rol">
                            {user.is_legal_agent && !isOnus
                              ? 'Representante Legal'
                              : t(`roles.${user.roles[0]}`)}
                          </span>
                        </p>
                      </Grid>
                      <Grid
                        id="user-roles"
                        item
                        xs={11}
                        sm={1}
                        md={1}
                        data-testid="user-roles-container"
                      >
                        <UserStatus testId="blocked-rol" className={classes.isNoMobile}>
                          {user.status}
                        </UserStatus>
                      </Grid>
                    </Grid>
                    <Grid item xs={1} sm={1} data-testid="opts-container">
                      <div className={classes.menuContainer}>{showDots(user)}</div>
                      <UserStatus testId="blocked-rol" className={classes.isMobile} isMobile>
                        {user.status}
                      </UserStatus>
                    </Grid>
                  </Grid>
                </Card>
              ))
            ) : (
              <EmptyList
                title={t(`users.emptyList.title`)}
                subTitle={t(`users.emptyList.subTitle`)}
              />
            )}
          </Grid>
          {confirmModal && (
            <BlockUserModal
              htmlIdCancelButton={
                getHtmlIdBlockUserModal(deleteBlockUser.action, deleteBlockUser.status).cancel
              }
              htmlIdConfirmButton={
                getHtmlIdBlockUserModal(deleteBlockUser.action, deleteBlockUser.status).confirm
              }
              open={confirmModal}
              data-testid="block-user-modal"
              testId="block-user-modal"
              textBody={setModalText()}
              textConfirm={t('common.accept')}
              confirmAction={handleConfirmBlock}
              toggleModal={() => setConfirmModal(false)}
            />
          )}
        </Grid>
      )}
    </>
  );
};

export default UserList;
